import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Select, message } from 'antd';
import { whatsappApi } from '../../utils/api';
import { supabase } from '../../utils/supabase';

const providerLabels = {
  whatchimp: 'WhatChimp',
  wati: 'WATI',
  wasenderapi: 'WasenderAPI',
  aisensy: 'AiSensy'
};

/**
 * Affiche et enregistre la connexion à une plateforme WhatsApp externe.
 * Les clés saisies partent exclusivement vers l'API serveur et ne sont jamais relues dans le navigateur.
 */
const WhatsAppApiIntegration = () => {
  const [form] = Form.useForm();
  const [adminForm] = Form.useForm();
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState('whatchimp');

  /**
   * Lit la boutique sélectionnée pour que les numéros d'administration soient
   * enregistrés avec la même boutique que l'intégration WhatsApp API.
   * @returns {string|null} Identifiant de la boutique active.
   */
  const getActiveShopId = () => window.localStorage.getItem('activeShopId');

  /**
   * Charge l'état public de l'intégration actuelle afin d'indiquer le fournisseur configuré.
   */
  const loadIntegration = async () => {
    try {
      const [{ data }, { data: { user } }] = await Promise.all([
        whatsappApi.getIntegration(),
        supabase.auth.getUser()
      ]);
      setIntegration(data.integration);
      if (data.integration?.provider) {
        setProvider(data.integration.provider);
        form.setFieldValue('provider', data.integration.provider);
      }
      if (user) {
        const shopId = getActiveShopId();
        if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer WhatsApp.');
        const { data: settings } = await supabase
          .from('settings')
          .select('admin_phone, admin_phone2')
          .eq('user_id', user.id)
          .eq('shop_id', shopId)
          .eq('type', 'whatsapp')
          .maybeSingle();
        adminForm.setFieldsValue(settings || {});
      }
    } catch (error) {
      message.error('Impossible de charger l’intégration WhatsApp API');
    }
  };

  /**
   * Enregistre les numéros d'administration déjà utilisés par les notifications WhatsApp.
   * Cette conservation permet aux notifications internes de fonctionner également avec une API externe.
   * @param {object} values Numéros renseignés dans le formulaire.
   */
  const saveAdminPhones = async (values) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Utilisateur non connecté');
      const shopId = getActiveShopId();
      if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer WhatsApp.');
      const { error } = await supabase.from('settings').upsert({
        user_id: user.id,
        shop_id: shopId,
        type: 'whatsapp',
        admin_phone: values.admin_phone.replace(/\D/g, ''),
        admin_phone2: values.admin_phone2?.replace(/\D/g, '') || null
      }, { onConflict: 'user_id,shop_id,type' });
      if (error) throw error;
      message.success('Numéros administrateurs sauvegardés');
    } catch (error) {
      message.error('Impossible de sauvegarder les numéros administrateurs');
    }
  };

  /**
   * Charge une première fois la configuration publique quand le composant est monté.
   */
  useEffect(() => {
    loadIntegration();
  }, []);

  /**
   * Envoie la nouvelle configuration au serveur puis efface les champs de secrets du navigateur.
   * @param {object} values Valeurs validées du formulaire Ant Design.
   */
  const saveIntegration = async (values) => {
    try {
      setLoading(true);
      const credentials = Object.fromEntries(
        Object.entries(values).filter(([key, value]) => key !== 'provider' && value)
      );
      const { data } = await whatsappApi.saveIntegration({
        provider: values.provider,
        credentials,
        is_active: true
      });
      setIntegration(data.integration);
      form.resetFields(['api_key']);
      message.success(`${providerLabels[values.provider]} est maintenant utilisé pour les envois WhatsApp`);
    } catch (error) {
      message.error(error.response?.data?.error || 'La configuration n’a pas pu être enregistrée');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Désactive le fournisseur API sans supprimer les identifiants enregistrés côté serveur.
   */
  const disableIntegration = async () => {
    try {
      setLoading(true);
      await whatsappApi.disableIntegration();
      setIntegration(null);
      message.success('Intégration API désactivée : vous pouvez utiliser la connexion directe');
    } catch (error) {
      message.error('Impossible de désactiver l’intégration API');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Adapte les champs visibles au fournisseur sélectionné, sans modifier les autres réglages WhatsApp.
   * @param {string} nextProvider Nouveau fournisseur choisi.
   */
  const handleProviderChange = (nextProvider) => {
    setProvider(nextProvider);
    form.resetFields(['api_key', 'phone_number_id', 'api_endpoint']);
  };

  /**
   * Copie l'URL publique du callback afin de la coller dans les réglages webhook du fournisseur.
   * L'URL est construite depuis l'API utilisée par l'application, évitant toute confusion entre
   * le domaine du backoffice et celui du serveur qui doit recevoir l'événement externe.
   */
  const copyWebhookUrl = async () => {
    const apiBaseUrl = String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const webhookUrl = `${apiBaseUrl}${integration.webhook_path}`;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      message.success('URL du webhook copiée');
    } catch (error) {
      message.error('Copie impossible : sélectionnez l’URL manuellement');
    }
  };

  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
      <h3 className="mb-1 text-base font-medium text-gray-900">Plateforme WhatsApp API</h3>
      <p className="mb-4 text-sm text-gray-600">
        Utilisez votre compte WhatChimp, WATI, WasenderAPI ou AiSensy au lieu de scanner un QR code.
      </p>

      {integration && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-white p-3 text-sm">
          <span>Plateforme active : <strong>{providerLabels[integration.provider]}</strong></span>
          <Button danger size="small" loading={loading} onClick={disableIntegration}>
            Désactiver
          </Button>
        </div>
      )}

      {integration?.webhook_path && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-gray-700">
          <p className="mb-2 font-medium">Alerte de déconnexion Telegram</p>
          <p className="mb-2">Dans votre fournisseur, activez le webhook de statut de session et utilisez cette URL :</p>
          <Input readOnly value={`${String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}${integration.webhook_path}`} addonAfter={<Button type="link" size="small" onClick={copyWebhookUrl}>Copier</Button>} />
          <p className="mb-0 mt-2 text-xs">WhatChimp ne semble pas proposer de webhook de déconnexion de session ; l’alerte reste disponible dès qu’un fournisseur envoie cet événement.</p>
        </div>
      )}

      <Form form={form} layout="vertical" onFinish={saveIntegration} initialValues={{ provider }}>
        <Form.Item name="provider" label="Fournisseur" rules={[{ required: true, message: 'Sélectionnez un fournisseur' }]}>
          <Select onChange={handleProviderChange}>
            {Object.entries(providerLabels).map(([value, label]) => <Select.Option key={value} value={value}>{label}</Select.Option>)}
          </Select>
        </Form.Item>

        <Form.Item name="api_key" label="Clé API" rules={[{ required: !integration, message: 'La clé API est requise' }]}>
          <Input.Password placeholder="Collez la clé API fournie par la plateforme" autoComplete="new-password" />
        </Form.Item>

        {provider === 'whatchimp' && (
          <Form.Item name="phone_number_id" label="ID du numéro WhatsApp" rules={[{ required: !integration, message: 'L’identifiant du numéro est requis' }]}>
            <Input placeholder="Phone Number ID WhatChimp" />
          </Form.Item>
        )}

        {provider === 'wati' && (
          <Form.Item name="api_endpoint" label="URL API WATI" rules={[{ required: !integration, message: 'L’URL API WATI est requise' }]}>
            <Input placeholder="https://live-mt-server.wati.io/..." />
          </Form.Item>
        )}

        <p className="mb-3 text-xs text-gray-500">
          Les messages texte nécessitent généralement une conversation ouverte depuis moins de 24 h. Les messages hors fenêtre doivent utiliser un modèle approuvé dans votre plateforme.
        </p>
        <Button type="primary" htmlType="submit" loading={loading}>
          {integration ? 'Mettre à jour la plateforme' : 'Activer la plateforme API'}
        </Button>
      </Form>

      {integration && (
        <Form className="mt-5 border-t border-blue-100 pt-4" form={adminForm} layout="vertical" onFinish={saveAdminPhones}>
          <p className="mb-3 text-sm font-medium text-gray-700">Numéros pour les notifications administrateur</p>
          <Form.Item name="admin_phone" label="Numéro principal" rules={[{ required: true, message: 'Le numéro principal est requis' }]}>
            <Input placeholder="Ex. 0701020304" />
          </Form.Item>
          <Form.Item name="admin_phone2" label="Numéro secondaire (optionnel)">
            <Input placeholder="Ex. 0701020304" />
          </Form.Item>
          <Button htmlType="submit">Sauvegarder les numéros</Button>
        </Form>
      )}
    </div>
  );
};

export default WhatsAppApiIntegration;
